'use client'

import Image from 'next/image'

interface CompanyPageProps {
  title: string
  subtitle: string
  launchYear: string
  mission: string
  problem: string
  vision: string
  solution: string
  story: string
  founderImage: string
  quote: string
}

export function CompanyPage({
  title,
  subtitle,
  launchYear,
  mission,
  problem,
  vision,
  solution,
  story,
  founderImage,
  quote,
}: CompanyPageProps) {
  return (
    <main className="min-h-screen bg-background">
      {/* Hero Section */}
      <section className="relative border-b border-border bg-gradient-to-b from-brand/5 to-background py-20 overflow-hidden">
        {/* Wave background - top */}
        <div className="absolute top-0 left-0 right-0 h-32 opacity-40">
          <Image
            src="/wave-bg.jpg"
            alt="Wave decoration"
            fill
            className="object-cover object-bottom"
            priority
          />
        </div>
        <div className="relative mx-auto max-w-4xl px-4 text-center z-10">
          <h1 className="mb-4 text-4xl font-bold leading-tight tracking-tight text-foreground md:text-5xl">
            {title}
          </h1>
          <p className="text-xl text-muted-foreground">{subtitle}</p>
        </div>
      </section>

      {/* Timeline + 4 Column Section */}
      <section className="mx-auto max-w-6xl px-4 py-20">
        <div className="mb-16 flex flex-col items-start gap-8 md:flex-row md:items-center">
          <div className="flex flex-col gap-2">
            <span className="text-sm font-semibold uppercase tracking-wider text-brand">Launched</span>
            <span className="text-4xl font-bold text-foreground">{launchYear}</span>
          </div>
          <div className="flex-1 border-l border-border pl-8">
            <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-4">
              <div>
                <h3 className="mb-3 text-sm font-semibold uppercase tracking-wider text-brand">Our Mission</h3>
                <p className="text-sm leading-relaxed text-muted-foreground">{mission}</p>
              </div>
              <div>
                <h3 className="mb-3 text-sm font-semibold uppercase tracking-wider text-brand">The Problem</h3>
                <p className="text-sm leading-relaxed text-muted-foreground">{problem}</p>
              </div>
              <div>
                <h3 className="mb-3 text-sm font-semibold uppercase tracking-wider text-brand">Our Vision</h3>
                <p className="text-sm leading-relaxed text-muted-foreground">{vision}</p>
              </div>
              <div>
                <h3 className="mb-3 text-sm font-semibold uppercase tracking-wider text-brand">How We Solved It</h3>
                <p className="text-sm leading-relaxed text-muted-foreground">{solution}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Our Story Section with Image */}
      <section className="mx-auto max-w-6xl px-4 py-20">
        <div className="grid gap-12 md:grid-cols-2 md:items-start">
          {/* Image with Founders Footer */}
          <div className="flex flex-col gap-8">
            <div className="relative overflow-hidden rounded-2xl">
              <Image
                src={founderImage}
                alt="Company founders"
                width={500}
                height={600}
                className="w-full object-cover"
                priority
              />
            </div>
            
            {/* Founders Info with LinkedIn Links */}
            <div className="grid grid-cols-2 gap-4">
              <a 
                href="https://www.linkedin.com/in/elvisenotie/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="group flex items-center gap-2 rounded-lg bg-muted p-4 transition-all hover:bg-brand/10"
              >
                <svg className="h-6 w-6 text-brand" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.225 0z"/>
                </svg>
                <div className="flex flex-col">
                  <span className="text-xs font-semibold uppercase text-brand">LinkedIn</span>
                  <span className="text-sm font-medium text-foreground">Elvis Enotie</span>
                  <span className="text-xs text-muted-foreground">COO</span>
                </div>
              </a>
              
              <a 
                href="https://www.linkedin.com/in/daniel-eze-9a1159155" 
                target="_blank" 
                rel="noopener noreferrer"
                className="group flex items-center gap-2 rounded-lg bg-muted p-4 transition-all hover:bg-brand/10"
              >
                <svg className="h-6 w-6 text-brand" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.225 0z"/>
                </svg>
                <div className="flex flex-col">
                  <span className="text-xs font-semibold uppercase text-brand">LinkedIn</span>
                  <span className="text-sm font-medium text-foreground">Eze Daniel</span>
                  <span className="text-xs text-muted-foreground">CEO</span>
                </div>
              </a>
            </div>
          </div>

          {/* Story Content */}
          <div>
            <h2 className="mb-6 text-3xl font-bold leading-tight text-foreground md:text-4xl">Our Story</h2>
            <p className="mb-6 leading-relaxed text-muted-foreground">{story}</p>
            <p className="text-sm leading-relaxed text-muted-foreground opacity-75">
              We're building the financial infrastructure that enables businesses and individuals to move money across borders
              seamlessly, with the speed of the internet and the security of modern technology.
            </p>
          </div>
        </div>
      </section>

      {/* Quote Section */}
      <section className="relative border-t border-border py-16 overflow-hidden">
        {/* Wave background - bottom */}
        <div className="absolute bottom-0 right-0 left-0 h-40 opacity-40 -z-10">
          <Image
            src="/wave-bg.jpg"
            alt="Wave decoration"
            fill
            className="object-cover object-top scale-y-[-1]"
            priority
          />
        </div>
        <div className="relative mx-auto max-w-4xl px-4 text-center z-10">
          <blockquote className="text-lg leading-relaxed text-foreground md:text-2xl">
            &quot;{quote}&quot;
          </blockquote>
        </div>
      </section>
    </main>
  )
}
